import Vue from 'vue';
import VueMqtt from 'vue-mqtt';

import store from '@/store';
import Type from '@/store/mutation-type';

import ctt from '@/utils/const/index';
import EnvUtils from '@/utils/EnvUtils';
import { getToken, noop } from '@/utils/utils';
import { toArray } from '@/utils/transform/index.js';
import { useApiStateManager } from '@/utils/api-utils';
import { isArray, isEmpty, isFunction } from '@/utils/is';
import { subDeviceDetail, unsubDeviceDetail, pushParam } from '@/api/mqtt.js';
import { getEventKey, register, setUserId, subscribe, resetMqtt } from '@/utils/mqtt/index.js';

const MAX_RETRY = 10;
const RETRY_DELAY = 300;

const initMqttApiManager = useApiStateManager({
  api: 'INIT_MQTT',
  log: EnvUtils.is('development', true)
});

let mqtt = null;
let vueMqtt = null;

export default {
  namespaced: true,
  state: {
    subscribeList: {},
    devicesDetail: {},
    deviceSnMap: {},
    deviceIdMap: {},
    organizeTree: [],
    deviceTypeInfo: {},

    alarmData: {}
  },
  mutations: {
    [Type.SET_DETAIL](state, data) {
      Vue.set(state.devicesDetail, data.sn, data);
    },
    [Type.CLEAR_DETAIL](state) {
      Vue.set(state, 'devicesDetail', {});
    },
    [Type.SET_BASIC_DATA](state, data) {
      Vue.set(state.deviceSnMap, data.sn, data);
      Vue.set(state.deviceIdMap, data.id, data);
    },
    [Type.CLEAR_BASIC_DATA](state) {
      Vue.set(state, 'deviceSnMap', {});
      Vue.set(state, 'deviceIdMap', {});
    },
    [Type.REMOVE_ALARM_BY_IDS](state, id) {
      const ids = toArray(id);
      ids.forEach(e => {
        Vue.delete(state.alarmData, e);
      });
    },
    [Type.MQTT_ALARM_HANDLED](state, data) {
      if (!isArray(data) || isEmpty(data)) return;

      data.forEach(alarmId => {
        Vue.delete(state.alarmData, alarmId);
      });
    },
    [Type.CLEAR_ALARM_DATA](state) {
      Object.keys(state.alarmData).forEach(alarmId => {
        Vue.delete(state.alarmData, alarmId);
      });
    },
    SET_SUBSCRIBE(state, { key, stop }) {
      Vue.set(state.subscribeList, key, stop);
    },
    CLEAR_SUBSCRIBES(state) {
      Vue.set(state, 'subscribeList', {});
    }
  },
  getters: {
    userId(_state, _getters, rootState) {
      return rootState.admin.userInfo.id;
    },
    alarmSnMap(state) {
      const list = Object.values(state.alarmData);
      return list.reduce((map, e) => {
        const target = (map[e.sn] = map[e.sn] || []);
        target.push(e);
        return map;
      }, {});
    }
  },
  actions: {
    [Type.MQTT_ALARM]({ state, rootState }, data) {
      const enableAlarmDisposition = rootState.device.monitorConfig.enableAlarmDisposition;
      if (!enableAlarmDisposition || isEmpty(data)) return;
      const { code, eventData } = data;
      if ([ctt.WS_DANGE1, ctt.WS_DANGE2].includes(code)) {
        const { id } = eventData;
        Vue.set(state.alarmData, id, eventData);
      }
    },

    /** 初始化mqtt */
    async initMqtt({ getters, commit, dispatch }, _retryCount = 0) {
      if (mqtt || !initMqttApiManager.isNeedUpdate()) return;

      if (_retryCount >= MAX_RETRY) {
        console.error(`[MQTT] init failed after ${MAX_RETRY} retries`);
        initMqttApiManager.setState('INITIALIZATION_FAILED');
        return;
      }

      initMqttApiManager.setState('INITIALZING');

      setUserId(getters.userId);

      mqtt = await register();

      if (!mqtt) {
        initMqttApiManager.setState('INITIALIZATION_FAILED');

        setTimeout(() => {
          dispatch('initMqtt', _retryCount + 1);
        }, RETRY_DELAY);

        return;
      }

      initMqttApiManager.setState('INITIALIZED');

      vueMqtt = new VueMqtt({
        mqtt,
        vuex: {
          store,
          actionPrefix: 'MQTT_',
          mutationPrefix: 'MQTT_'
        },
        options: {
          eventMapHandler: k => getEventKey(k)
        }
      });

      Vue.use(vueMqtt);

      commit(`socket/${Type.INTERVAL_UP}`, null, { root: true });

      dispatch('initSubscriptions');
    },

    /** 订阅设备详情 */
    subDeviceDetail({ state, getters, commit, dispatch }, { sn }) {
      const userId = getters.userId;

      if (!userId || state.subscribeList['detail']) return noop;

      const snList = toArray(sn);

      subDeviceDetail(snList);

      snList.forEach(e => pushParam({ sn: e }));

      if (!state.subscribeList['details']) {
        const stop = subscribe(`{prefix}/{userId}/monitor/detail/`, (_, msg) => {
          commit(`socket/${Type.SUB_DEVICE}`, msg, { root: true });
        });
        commit('SET_SUBSCRIBE', { key: 'details', stop });
      }

      return () => dispatch('mqtt/unsubDeviceDetail', { sn }, { root: true });
    },

    /** 取消订阅设备详情 */
    unsubDeviceDetail(_, { sn }) {
      const sns = toArray(sn);
      const token = getToken();

      if (!isEmpty(sns) && !isEmpty(token)) {
        unsubDeviceDetail(sns);
      }
    },

    /** 订阅设备基础信息 状态 */
    subDeviceBasicData({ state, getters, commit }) {
      const userId = getters.userId;

      if (!userId || state.subscribeList['basic']) return;

      const stop = subscribe(`{prefix}/{userId}/basic/`, (_, data) => {
        commit(`socket/${Type.UPDATE_DEVICE_TREE}`, data, { root: true });
        commit(Type.SET_BASIC_DATA, data);
      });

      commit('SET_SUBSCRIBE', { key: 'basic', stop });
    },

    initSubscriptions({ state, getters, commit, dispatch }) {
      const userId = getters.userId;
      if (!userId) return;

      dispatch('subDeviceBasicData');

      const topicDefs = [
        { key: 'detect/detail/', topic: '{prefix}/{userId}/detect/detail/' },
        { key: 'alarm/', topic: '{prefix}/{userId}/alarm/' },
        { key: 'alarm/dispose/finish/', topic: '{prefix}/{userId}/alarm/dispose/finish/' }
      ];

      topicDefs.forEach(({ key, topic }) => {
        if (!state.subscribeList[key]) {
          const stop = subscribe(topic);
          commit('SET_SUBSCRIBE', { key, stop });
        }
      });
    },

    clearAll({ state, commit }) {
      const list = Object.values(state.subscribeList);
      list.forEach(unsub => {
        isFunction(unsub) && unsub();
      });

      commit('CLEAR_SUBSCRIBES');
      mqtt = null;
      vueMqtt = null;

      initMqttApiManager.setState('NOT_INIT');

      resetMqtt();
      commit(Type.CLEAR_DETAIL);
      commit(Type.CLEAR_BASIC_DATA);
    }
  }
};
